import 'react-datepicker/dist/react-datepicker.css'
import '../DatePicker/DatePicker.css'

import { ChevronUpDownIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import { endOfDay, startOfDay } from 'date-fns'
import { ComponentPropsWithoutRef, forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import ReactDatePicker from 'react-datepicker'

import { DatePeriod } from '../MonthPicker/MonthPicker'
import { DayPickerHeader } from './DayPickerHeader'

export interface DateInterval {
  start: Date
  end: Date
}

export interface DayPickerProps {
  value?: Date | null
  onChange: (date: Date) => void
  placeholder?: string
  className?: string
  disabled?: boolean

  showTimeSelect?: boolean
  timeIntervals?: number
  monthsShown?: number
  showTimeInput?: boolean

  excludeDates?: Date[]
  excludeDateIntervals?: DateInterval[]
  filterDate?: (date: Date) => boolean

  excludeTimes?: Date[]
  filterTime?: (date: Date) => boolean

  shouldCloseOnSelect?: boolean
  showMonthAndYearSelector?: boolean
  timeFormat?: string
  dateFormat?: string

  maxDate?: Date | null
  minDate?: Date | null

  onPeriodChange?: (period: DatePeriod) => void
}

export function DayPicker({
  value,
  showMonthAndYearSelector,
  placeholder,
  timeFormat,
  showTimeInput,
  onChange,
  onPeriodChange,
  ...props
}: DayPickerProps) {
  const datePickerRef = useRef<ReactDatePicker>(null)
  const getDayPeriod = (date: Date) => {
    const start = startOfDay(date)
    const end = endOfDay(date)

    return {
      start: start.toISOString(),
      end: end.toISOString(),
    } as DatePeriod
  }

  const onDateChange = (date: Date) => {
    onChange && onChange(date)
    onPeriodChange && onPeriodChange(getDayPeriod(date))
  }

  useEffect(() => {
    const yesterday = getYesterdayDate()
    onChange && onChange(yesterday)
    onPeriodChange && onPeriodChange(getDayPeriod(yesterday))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isYesterdaySelected = () => {
    if (!value) return false

    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)

    return yesterday.toDateString() === value.toDateString()
  }

  return (
    <div className='root-defaultView'>
      <ReactDatePicker
        ref={datePickerRef}
        selected={value}
        showPopperArrow={false}
        customInput={<CustomInputSelect isYesterdaySelected={isYesterdaySelected()} />}
        calendarClassName={classNames(
          'shadow-xl ring-1 ring-black ring-opacity-5 p-6 border-0 rounded-md',
          showTimeInput && 'flex-col'
        )}
        renderCustomHeader={(props) => (
          <DayPickerHeader
            {...props}
            showMonthAndYearSelector={showMonthAndYearSelector ?? false}
            onYesterdaySelect={(yesterday) => {
              onDateChange(yesterday)
              datePickerRef?.current?.setOpen(false)
            }}
          />
        )}
        placeholderText={placeholder}
        showTimeInput={showTimeInput}
        timeFormat={timeFormat ?? 'hh:mm a'}
        onChange={onDateChange}
        {...props}
      />
    </div>
  )
}

export type CustomInputSelectProps = ComponentPropsWithoutRef<'button'> & {
  isYesterdaySelected?: boolean
}

const CustomInputSelect = forwardRef<HTMLButtonElement, CustomInputSelectProps>(function CustomInputSelect(
  { value, isYesterdaySelected, ...props },
  ref
) {
  const innerRef = useRef<HTMLButtonElement>(null)
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  useImperativeHandle(ref, () => innerRef.current!)

  return (
    <div
      className='mt-1 flex w-36 cursor-pointer items-center justify-between rounded-md border border-brand-gray-300 bg-white px-3 py-2 font-inter text-sm text-brand-gray-900 shadow-sm'
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        innerRef.current?.focus()
      }}
      aria-label='open calendar modal'
    >
      <button ref={innerRef} {...props}>
        {isYesterdaySelected ? 'Yesterday' : value}
      </button>

      <ChevronUpDownIcon className='h-5 w-5 font-bold text-brand-gray-400' aria-hidden='true' />
    </div>
  )
})

export const getYesterdayDate = () => {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  return yesterday
}
